import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Settings2, ShoppingCart } from "lucide-react";
import { PageHeader } from "@/components/layout/Layout";
import { Button } from "@/components/ui";
import { trpc } from "@/lib/trpc";
import { useAppStore } from "@/stores/app.store";
import { openBlankShopWindow } from "@/lib/shopCart";
import { ConnectorDialog } from "@/pages/Articles/ConnectorDialog";

/** Händlershops (IDS Connect): ein Klick in den Shop zum Stöbern; der Warenkorb kommt als neues Angebot zurück. */
export function Shops() {
  const navigate = useNavigate();
  const addToast = useAppStore((s) => s.addToast);
  const shops = trpc.connector.shops.useQuery();
  const start = trpc.connector.startPunchout.useMutation();
  const [busy, setBusy] = useState<string | null>(null);
  const [setup, setSetup] = useState<string | null>(null);

  const open = (shop: { id: string; name: string }) => {
    setBusy(shop.id);
    const w = openBlankShopWindow();
    start.mutate(
      { supplierId: shop.id, returnTo: "new" },
      {
        onSuccess: (r) => {
          if (w) w.location.href = r.redirectUrl;
          else window.open(r.redirectUrl, "_blank");
        },
        onError: (e) => {
          w?.close();
          addToast({
            type: "error",
            title: "Shop konnte nicht geöffnet werden",
            description: e.message,
          });
        },
        onSettled: () => setBusy(null),
      },
    );
  };

  const list = shops.data ?? [];

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Händlershops" />
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <p className="text-sm text-text-secondary max-w-2xl">
          Ein Klick öffnet den Shop des Händlers in einem neuen Fenster. Artikel
          in den Warenkorb legen und dort „An Handwerkersoftware senden“
          drücken: Der Warenkorb landet dann als <b>neues Angebot</b> hier im
          CRM. Mehrere Händler in <i>einem</i> Angebot sammelst du direkt im
          Angebot über „Aus Händlershop“.
        </p>

        {shops.isLoading && (
          <p className="text-sm text-text-tertiary">Lädt …</p>
        )}

        {!shops.isLoading && !list.length && (
          <div className="card p-4 max-w-xl text-sm space-y-2">
            <p>Noch kein Händler mit IDS Connect eingerichtet.</p>
            <Button variant="secondary" onClick={() => navigate("/artikel")}>
              Zu Artikel → Lieferanten
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 max-w-5xl">
          {list.map((s) => (
            <div key={s.id} className="card p-4 flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-text-tertiary" />
                <span className="text-text-primary font-semibold">
                  {s.name}
                </span>
              </div>
              <p className="text-xs text-text-tertiary">
                {s.ready
                  ? "Zugang eingerichtet"
                  : `Zugang unvollständig (fehlt: ${s.missing.join(", ")})`}
              </p>
              <div className="flex gap-2 mt-auto">
                <Button
                  variant="primary"
                  disabled={!s.ready}
                  loading={busy === s.id}
                  onClick={() => open(s)}
                >
                  <ExternalLink className="w-4 h-4" /> Shop öffnen
                </Button>
                <Button variant="ghost" onClick={() => setSetup(s.id)}>
                  <Settings2 className="w-4 h-4" /> Zugang
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ConnectorDialog
        supplierId={setup}
        open={!!setup}
        onClose={() => {
          setSetup(null);
          shops.refetch();
        }}
      />
    </div>
  );
}
